"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

interface Props {
  label: string;
  /**
   * Where to send visitors when CookieYes has no UI to open. Not a consent
   * control — an information path, so the entry point is never a dead click.
   */
  fallbackHref?: string;
}

/** Probe result for whether CookieYes can actually open its consent UI. */
type Mode = "probing" | "cmp" | "fallback";

// How long to wait for the SDK to report a consent law before falling back.
// It normally boots well under a second; this only has to outlast a slow
// network. 20 × 250ms = 5s.
const PROBE_ATTEMPTS = 20;
const PROBE_INTERVAL_MS = 250;

// Footer "Cookie Settings" entry point. The label is ALWAYS rendered,
// wherever the visitor is — the privacy policy promises this control is
// available, so it must not disappear based on geography.
//
// What it does adapts to what CookieYes offers, because that genuinely
// differs by region:
// - Regions covered by the dashboard's geo-targeting: `activeLaw` is set and
//   the SDK has a preference center. The `cky-banner-element` class is
//   CookieYes's own supported reopen hook, so the click opens it.
// - Regions NOT covered: CookieYes applies an implied grant and initialises
//   no UI at all (`activeLaw` is "", `performBannerAction` throws, no
//   `.cky-*` nodes exist). Same when the SDK is blocked by an ad blocker or
//   on localhost, where it domain-gates itself. There the button becomes a
//   link to the privacy policy instead of a click that silently does nothing.
//
// If a real opt-out is wanted in the non-targeted regions too, that is a
// CookieYes dashboard change (serve those regions an opt-out/notice banner
// instead of nothing), not a change here.
export default function CookieSettingsButton({
  label,
  fallbackHref = "/legal/privacy-policy",
}: Props) {
  const [mode, setMode] = useState<Mode>("probing");

  useEffect(() => {
    let attempts = PROBE_ATTEMPTS;
    let timer: number | undefined;
    const check = () => {
      try {
        if (window.getCkyConsent && window.getCkyConsent().activeLaw !== "") {
          setMode("cmp");
          return;
        }
      } catch {
        // SDK mid-boot — fall through to retry.
      }
      attempts -= 1;
      if (attempts > 0) {
        timer = window.setTimeout(check, PROBE_INTERVAL_MS);
      } else {
        setMode("fallback");
      }
    };
    check();
    return () => window.clearTimeout(timer);
  }, []);

  const className =
    "text-muted-foreground hover:text-foreground cursor-pointer text-left text-xs whitespace-nowrap transition-colors";

  if (mode === "fallback") {
    return (
      <Link href={fallbackHref} className={className}>
        {label}
      </Link>
    );
  }

  // "probing" renders the CMP trigger too: the SDK is the expected case, and
  // waiting would leave the control missing for the first moments of the page.
  return (
    <button type="button" className={`cky-banner-element ${className}`}>
      {label}
    </button>
  );
}
